import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useRecaptcha } from "@/lib/recaptcha";

export default function Contact() {
  const { t } = useTranslation();
  const { execute } = useRecaptcha();
  const [f, setF] = useState({ name: "", organization: "", phone: "", email: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recaptcha_token = await execute("contact");
      await api.post("/enquiries", { ...f, recaptcha_token });
      toast.success(t("enquiry.success"));
      setF({ name: "", organization: "", phone: "", email: "", message: "", website: "" });
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-700">Get in Touch</div>
      <h1 className="mt-3 text-4xl font-bold tracking-tight text-[#0F2942] sm:text-5xl" style={{ fontFamily: "Outfit, sans-serif" }}>{t("contact.title")}</h1>
      <p className="mt-3 max-w-2xl text-lg text-slate-600">{t("contact.subtitle")}</p>

      <div className="mt-12 grid gap-10 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="space-y-5">
            {[
              { i: Phone, l: t("contact.phone"), v: "+91 78755 55877" },
              { i: MessageCircle, l: t("contact.whatsapp"), v: "+91 78755 55877" },
              { i: Mail, l: t("contact.email"), v: "hicarelabsolutions@gmail.com" },
              { i: MapPin, l: t("contact.address"), v: "Pune, Maharashtra, India" },
            ].map(({ i: Icon, l, v }) => (
              <div key={l} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50">
                  <Icon className="h-5 w-5 text-teal-700" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="mt-0.5 font-semibold text-[#0F2942]">{v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-8 lg:col-span-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("enquiry.name")}</Label><Input required data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>{t("enquiry.org")}</Label><Input data-testid="contact-org" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} /></div>
            <div><Label>{t("enquiry.phone")}</Label><Input data-testid="contact-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div><Label>{t("enquiry.email")}</Label><Input required type="email" data-testid="contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          </div>
          <div className="mt-4"><Label>{t("enquiry.message")}</Label><Textarea required rows={5} data-testid="contact-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></div>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                 value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })}
                 style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
          <Button type="submit" disabled={busy} data-testid="contact-submit" className="mt-5 w-full bg-[#0F2942] text-white hover:bg-[#0A192F]">
            {busy ? "Sending…" : t("enquiry.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
